import React, { useMemo, useEffect } from 'react';
import { ApolloProvider } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import { createAuth0ApolloClient, clearApolloClient } from './auth0-client';

interface Auth0ApolloProviderProps {
  children: React.ReactNode;
}

const Auth0ApolloProvider: React.FC<Auth0ApolloProviderProps> = ({
  children,
}) => {
  const { getIdTokenClaims, isAuthenticated, isLoading, error } = useAuth0();

  const client = useMemo(() => {
    // For unauthenticated users, create a client that won't attempt token exchange
    if (!isAuthenticated || isLoading) {
      return createAuth0ApolloClient(() => Promise.resolve({}));
    }

    // For authenticated users, create a client with token exchange (same as crusher)
    if (getIdTokenClaims) {
      return createAuth0ApolloClient(getIdTokenClaims);
    }

    return createAuth0ApolloClient(() => Promise.resolve({}));
  }, [isAuthenticated, isLoading, getIdTokenClaims]);

  useEffect(() => {
    if (error) {
      // Error is handled by Auth0 SDK and UI components
    }
  }, [error]);

  // Clear client when user logs out (same as crusher)
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      clearApolloClient();
    }
  }, [isAuthenticated, isLoading]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Auth0ApolloProvider;
